export type PuzzleManifestEntry = {
  id: string;
  date: string;
  path: string;
};

export type PuzzleManifest = {
  version: number;
  timezonePolicy?: 'local-date';
  puzzles: PuzzleManifestEntry[];
};

export type DailyPuzzleResolution = {
  entry: PuzzleManifestEntry;
  date: string;
  exact: boolean;
  fallbackReason: 'no-exact-date' | 'before-first-date' | null;
};

const DAY_MS = 86_400_000;

export function resolveDailyPuzzle(manifest: PuzzleManifest, todayDateString: string): DailyPuzzleResolution {
  const entries = normalizeEntries(manifest);
  if (entries.length === 0) {
    throw new Error('Puzzle manifest has no puzzle entries');
  }

  const exact = entries.find((entry) => entry.date === todayDateString);
  if (exact) {
    return {
      entry: exact,
      date: todayDateString,
      exact: true,
      fallbackReason: null
    };
  }

  const published = entries.filter((entry) => entry.date <= todayDateString);
  if (published.length === 0) {
    return {
      entry: entries[0],
      date: todayDateString,
      exact: false,
      fallbackReason: 'before-first-date'
    };
  }

  const firstDate = published[0].date;
  const daysSinceStart = Math.max(0, Math.floor((dateToUtcMs(todayDateString) - dateToUtcMs(firstDate)) / DAY_MS));
  const entry = published[daysSinceStart % published.length];

  return {
    entry,
    date: todayDateString,
    exact: false,
    fallbackReason: 'no-exact-date'
  };
}

function normalizeEntries(manifest: PuzzleManifest): PuzzleManifestEntry[] {
  return [...(manifest.puzzles ?? [])].sort((a, b) => a.date.localeCompare(b.date));
}

function dateToUtcMs(dateString: string): number {
  const [year, month, day] = dateString.split('-').map(Number);
  return Date.UTC(year, month - 1, day);
}
